import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/Button';
import closeWhiteIcon from '@/assets/close_white.svg';
import chevronLeft from '@/assets/chevron_left.svg';
import chevronRight from '@/assets/chevron_right.svg';
import cursorMagnifier from '@/assets/cursor_magnifier.svg';
import cursorHand from '@/assets/cursor_hand.svg';
import warningIcon from '@/assets/warning_white.svg';
import logoIcon from '@/assets/logo_white.svg';

const ImagePreviewWithRequest = ({
  images = [],
  initialIndex = 0,
  onClose,
  onRequestImages,
  showRequestFeature = false,
  className = ""
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isActiveDrag, setIsActiveDrag] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  
  const imageRef = useRef(null);
  const containerRef = useRef(null);
  const positionRef = useRef({ x: 0, y: 0 });
  
  const dragThreshold = 5;
  
  // Memoize cursor styles
  const cursorStyles = useMemo(() => ({
    magnifier: `url("${cursorMagnifier}") 12 12, zoom-in`,
    hand: `url("${cursorHand}") 12 12, grab`
  }), []);

  // Reset state when images or initialIndex changes
  useEffect(() => {
    setCurrentIndex(initialIndex);
    setIsZoomed(false);
    setIsDragging(false);
    setIsActiveDrag(false);
    setPosition({ x: 0, y: 0 });
    positionRef.current = { x: 0, y: 0 };
  }, [images, initialIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (images.length > 1) {
        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            handlePrev();
            break;
          case 'ArrowRight':
            event.preventDefault();
            handleNext();
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [images.length, currentIndex]);

  // Preload adjacent images
  const preloadAdjacentImages = useCallback(() => {
    if (!images.length) return;
    
    const preloadImage = (src) => {
      const img = new Image();
      img.src = src;
    };
    
    // Preload next image
    const nextIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
    if (images[nextIndex]) preloadImage(images[nextIndex]);
    
    // Preload previous image
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
    if (images[prevIndex]) preloadImage(images[prevIndex]);
  }, [images, currentIndex]);

  useEffect(() => {
    preloadAdjacentImages();
  }, [currentIndex, preloadAdjacentImages]);

  // Use requestAnimationFrame for smooth dragging
  const updatePosition = useCallback((x, y) => {
    if (!imageRef.current) return;
    
    positionRef.current = { x, y };
    
    requestAnimationFrame(() => {
      setPosition({ x, y });
    });
  }, []);

  const handleImageClick = useCallback(() => {
    if (!isDragging) {
      if (isZoomed) {
        updatePosition(0, 0);
      }
      setIsZoomed(prevZoomed => !prevZoomed);
    }
  }, [isDragging, isZoomed, updatePosition]);

  const handleMouseDown = useCallback((e) => {
    if (isZoomed) {
      setDragStart({ 
        x: e.clientX - positionRef.current.x, 
        y: e.clientY - positionRef.current.y,
        initialX: e.clientX,
        initialY: e.clientY
      });
      setIsDragging(false);
    }
  }, [isZoomed]);

  const handleMouseMove = useCallback((e) => {
    if (dragStart && isZoomed) {
      const deltaX = Math.abs(e.clientX - dragStart.initialX);
      const deltaY = Math.abs(e.clientY - dragStart.initialY);
      
      if (deltaX > dragThreshold || deltaY > dragThreshold) {
        if (!isDragging) {
          setIsDragging(true);
          setIsActiveDrag(true);
        }
      }
      
      updatePosition(e.clientX - dragStart.x, e.clientY - dragStart.y);
    }
  }, [dragStart, isZoomed, updatePosition, dragThreshold, isDragging]);

  const handleMouseUp = useCallback(() => {
    setDragStart(null);
    setIsActiveDrag(false);
    
    setTimeout(() => {
      setIsDragging(false);
    }, 50);
  }, []);

  const handlePrev = useCallback((e) => {
    if (e) e.stopPropagation();
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    
    // Reset zoom and position when changing images
    setIsZoomed(false);
    setIsActiveDrag(false);
    updatePosition(0, 0);
  }, [images.length, updatePosition]);

  const handleNext = useCallback((e) => {
    if (e) e.stopPropagation();
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    
    // Reset zoom and position when changing images
    setIsZoomed(false);
    setIsActiveDrag(false);
    updatePosition(0, 0);
  }, [images.length, updatePosition]);

  // Handle mouse leave to stop dragging
  useEffect(() => {
    const handleMouseLeave = () => {
      setDragStart(null);
      setIsDragging(false);
      setIsActiveDrag(false);
    };

    const currentContainer = containerRef.current;
    if (currentContainer) {
      currentContainer.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (currentContainer) {
        currentContainer.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  // Memoize transform style for better performance
  const imageTransform = useMemo(() => {
    if (isZoomed) {
      return {
        transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(2)`,
        transformOrigin: 'center center',
        willChange: 'transform'
      };
    }
    return {
      transform: 'none',
      transformOrigin: 'center center',
      willChange: 'auto'
    };
  }, [isZoomed, position.x, position.y]);

  const currentImage = images[currentIndex];
  const hasImages = images.length > 0;

  return (
    <div className={`flex-1 self-stretch relative overflow-hidden ${className}`} ref={containerRef}>
      {/* Request feature banner - always show when enabled, positioned at the very top */}
      {showRequestFeature && (
        <div className="w-full px-6 py-2.5 bg-black/90 flex justify-center items-center gap-5 z-30">
          <div className="flex justify-start items-start gap-2">
            <div className="w-4 self-stretch pt-[2.50px] flex flex-col justify-start items-start gap-2.5">
              <div className="self-stretch h-4 relative overflow-hidden">
                <img src={warningIcon} alt="warning" className="w-[14.40px] h-[14.40px] left-[0.80px] top-[0.80px] absolute" />
              </div>
            </div>
            <div className="flex-1 justify-center text-white text-sm">
              {t('psaInventory.imageRequestMessage')}
            </div>
          </div>
          <Button
            variant="primary"
            size="s" 
            className="h-7 px-3.5 bg-white rounded-3xl"
            onClick={onRequestImages}
          >
            <div className="text-center justify-center text-[#111111] text-xs">
              {t('psaInventory.requestButton')}
            </div>
          </Button>
        </div>
      )}

      {hasImages ? (
        <>
          {/* Close Button - positioned below the request banner */}
          {onClose && (
            <Button 
              variant="default"
              size="m"
              className="!bg-[#00000000] !border-[#FFFFFF33] absolute right-11 w-12 !h-12 rounded-full z-20 !px-0 top-[80px]"
              onClick={onClose}
            >
              <img src={closeWhiteIcon} className='h-5 w-5' alt="Close" />
            </Button>
          )}
          
          {/* Large image display */}
          <div 
            className="w-full h-full flex items-center justify-center relative"
            style={{ cursor: isZoomed ? cursorStyles.hand : cursorStyles.magnifier, maxHeight: 'calc(100vh - 48px)' }}
            onClick={handleImageClick}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <img 
              ref={imageRef}
              src={currentImage}
              alt="Preview" 
              className={`max-w-[609px] w-auto object-contain rounded ${isActiveDrag ? '' : 'transition-transform duration-200'} ${isZoomed ? 'max-w-none' : ''}`}
              style={{
                ...imageTransform,
                // When viewport height >= 1070px → max height = 1022px
                // When viewport height < 1070px → height = 100vh - 48px
                maxHeight: 'min(1022px, calc(100vh - 48px))',
              }}
              draggable="false"
              loading="eager"
            />
            
            {/* Image indicator at bottom */}
            {images.length > 1 && (
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-10">
                <div className="h-[35px] px-6 bg-black/90 rounded-[46px] inline-flex justify-center items-center gap-2.5">
                  <div className="text-white text-base font-normal font-['Roboto'] leading-normal">
                    {currentIndex + 1}/{images.length}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation buttons for multiple images */}
          {images.length > 1 && (
            <>
              <Button 
                variant="default"
                size="m"
                onClick={handlePrev}
                className="w-12 !h-12 rounded-full !px-0 left-[44px] top-1/2 transform -translate-y-1/2 absolute"
              >
                <img src={chevronLeft} alt="Previous" className="w-6 h-6" />
              </Button>
              <Button 
                variant="default"
                size="m"
                onClick={handleNext}
                className="w-12 !h-12 rounded-full !px-0 right-[44px] top-1/2 transform -translate-y-1/2 absolute"
              >
                <img src={chevronRight} alt="Next" className="w-6 h-6" />
              </Button>
            </>
          )}

        </>
      ) : (
        // No images placeholder
        <div className="w-full h-full flex flex-col justify-center items-center gap-2.5">
          <div className="w-12 h-12 relative">
            <img src={logoIcon} alt="logo" className="w-12 h-12" />
          </div>
          <div className="self-stretch text-center justify-center text-white text-sm">
            {t('psaInventory.noImageUploaded')}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagePreviewWithRequest;
